import React, { useEffect, useMemo, useState } from "react";
import DetailSaveStatus from "../components/DetailSaveStatus";
import EntityPageScaffold from "../components/entity/EntityPageScaffold";
import AssetManagementSectionTabs from "../components/AssetManagementSectionTabs";
import { ASSET_TAB_RENTAL_LEASES } from "../shared/utils/assetManagementTabs";
import { INVESTMENT_HOME_ASSET_CATEGORY } from "../shared/constants/rentalAsset";
import CrudListToolbar from "../components/entity/CrudListToolbar";
import ImportCsvPanel from "../components/entity/ImportCsvPanel";
import ColumnCustomizerModal from "../components/ColumnCustomizerModal";
import { TableActionColumnHeader } from "../components/TableShell";
import LeaseTenantsModal from "../components/LeaseTenantsModal";
import BulkUpdateRentalLeasesModal from "../components/BulkUpdateRentalLeasesModal";
import RentalLeaseSummaryStatCards from "../components/RentalLeaseSummaryStatCards";
import EntityCardPeekOverlay, { getPeekAnchorRect } from "../components/entity/EntityCardPeekOverlay";
import AssetEntityCard from "../components/AssetEntityCard";
import SearchableSelect from "../components/SearchableSelect";
import useBlurAutoSave from "../shared/hooks/useBlurAutoSave";
import { useEntityListChrome } from "../shared/hooks/useEntityListChrome";
import { formatBatchSummaryMessage } from "../shared/utils/batchSummaryMessage";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import { bulkSequentialDelete } from "../shared/utils/bulkSequentialDelete";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import { IconEdit, IconDelete, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import { getAssetLabel } from "../shared/utils/assetLabel";
import { parseCsvDateToIso } from "../shared/utils/csvDate";
import { parseOptionalFiniteNumber } from "../shared/utils/csvNumber";
import { parseCsvTextToNormalizedTable } from "../shared/utils/csvParse";
import {
  attachValidatedCsvFileImport,
  downloadCsvTextFile,
  downloadCsvTextFromString,
} from "../shared/utils/csvFileHelpers";
import {
  RENTAL_LEASE_CSV_HEADER_LINE,
  buildRentalLeaseCsvExportText,
  findLeaseCsvColumnIndex,
} from "../shared/utils/rentalLeaseCsv";
import {
  formatTenantNamesDisplay,
  normalizeTenantsList,
  parseTenantsDisplayText,
  validateTenantsList,
} from "../shared/utils/rentalLeaseTenants";
import {
  RENTAL_LEASE_STATUS_OPTIONS,
  RENTAL_LEASE_TABLE_COLUMNS,
  RENTAL_LEASE_TABLE_DEFAULT_VISIBLE,
  RENTAL_LEASE_TABLE_KEY,
  RENTAL_LEASE_SORT_KEY_TYPES,
  RENTAL_LEASE_NUMERIC_COLUMN_IDS,
  isLeaseExpiredByEndDate,
  summarizeLeaseRent,
} from "../shared/utils/rentalLeaseTable";
import { fetchTablePreferences, saveTablePreferences } from "../shared/api/tablePreferences";
import { bulkUpdateRentalLeases } from "../shared/api/rentalLease";
import { formatDueDate, getDueDatePart } from "../shared/utils/dueDateTime";
import { formatCurrencyUsd } from "../shared/utils/financeDisplay";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");

function toYmdString(value) {
  return getDueDatePart(value) || "";
}

function formatLeaseMoney(amountLike) {
  if (amountLike == null || amountLike === "") return "—";
  const n = Number(amountLike);
  if (!Number.isFinite(n)) return "—";
  return formatCurrencyUsd(n);
}

function formatYmdDisplay(ymd) {
  const s = ymd != null ? String(ymd).trim() : "";
  if (!s) return "";
  return formatDueDate(`${s}T00:00:00.000Z`) || s;
}

function normalizeStatus(value) {
  const raw = value != null ? String(value).trim() : "";
  if (!raw) return "Upcoming";
  const match = RENTAL_LEASE_STATUS_OPTIONS.find((v) => v.toLowerCase() === raw.toLowerCase());
  return match || "Upcoming";
}

function normalizeNumber(value) {
  if (value == null) return "";
  const t = String(value).trim();
  if (!t) return "";
  const n = Number(t);
  return Number.isFinite(n) ? String(n) : "";
}

function RentalLease() {
  const [leases, setLeases] = useState([]);
  const [assetList, setAssetList] = useState([]);
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo?.account_id;
  const currentUserId = userInfo?._id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [statusFilter, setStatusFilter] = useState("");
  const [expirationQuickFilter, setExpirationQuickFilter] = useState(""); // "" | "expired"
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [linkedPeek, setLinkedPeek] = useState(null);
  const [visibleColumnIds, setVisibleColumnIds] = useState(() => [...RENTAL_LEASE_TABLE_DEFAULT_VISIBLE]);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [columnPrefsSaving, setColumnPrefsSaving] = useState(false);

  const {
    selectedIds,
    setSelectedIds,
    deleting,
    setDeleting,
    searchTerm,
    setSearchTerm,
    sortConfig,
    bannerError,
    setBannerError,
    viewMode,
    setViewMode,
    confirmDialog,
    setConfirmDialog,
    closeConfirmDialog,
    handleSort,
    handleSelectAll,
    handleSelectOne,
    importPanelExpanded,
    setImportPanelExpanded,
  } = useEntityListChrome({ initialViewMode: "table" });

  const [detailId, setDetailId] = useState(null);
  const [detailMode, setDetailMode] = useState("");
  const [detailPropertyAddress, setDetailPropertyAddress] = useState("");
  const [detailAssetId, setDetailAssetId] = useState("");
  const [detailStartDate, setDetailStartDate] = useState("");
  const [detailEndDate, setDetailEndDate] = useState("");
  const [detailRentAmount, setDetailRentAmount] = useState("");
  const [detailSecurityDeposit, setDetailSecurityDeposit] = useState("");
  const [detailPetDeposit, setDetailPetDeposit] = useState("");
  const [detailStatus, setDetailStatus] = useState("Upcoming");
  const [detailTenants, setDetailTenants] = useState([]);
  const [detailLandlords, setDetailLandlords] = useState("");
  const [detailActiveField, setDetailActiveField] = useState("");
  const [detailInitialValues, setDetailInitialValues] = useState(null);
  const [tenantsModalOpen, setTenantsModalOpen] = useState(false);
  const [tenantsModalLeaseId, setTenantsModalLeaseId] = useState(null);
  const [tenantsModalSaving, setTenantsModalSaving] = useState(false);

  useEffect(() => {
    refreshAll();
    let cancelled = false;
    async function loadPrefs() {
      if (!account_id || !currentUserId) return;
      try {
        const ids = await fetchTablePreferences(account_id, currentUserId, RENTAL_LEASE_TABLE_KEY);
        if (cancelled) return;
        if (Array.isArray(ids) && ids.length > 0) {
          const allowed = new Set(RENTAL_LEASE_TABLE_COLUMNS.map((c) => c.id));
          const filtered = ids.filter((id) => allowed.has(id));
          if (filtered.length > 0) setVisibleColumnIds(filtered);
        }
      } catch {
        // keep defaults
      }
    }
    loadPrefs();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  function refreshAll() {
    if (!account_id) return;
    Promise.all([
      apiFetch(serverUrl + "/listAssets", {
        method: "POST",
        body: JSON.stringify({ account_id, category: INVESTMENT_HOME_ASSET_CATEGORY }),
      }).then((resp) => resp.json()),
      apiFetch(serverUrl + "/listRentalLeases", {
        method: "POST",
        body: JSON.stringify({ account_id }),
      }).then((resp) => resp.json()),
    ])
      .then(([assets, leaseList]) => {
        setAssetList(Array.isArray(assets) ? assets : []);
        setLeases(Array.isArray(leaseList) ? leaseList : []);
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  const assetById = useMemo(() => {
    const map = {};
    (assetList || []).forEach((a) => {
      if (a && a._id) map[String(a._id)] = a;
    });
    return map;
  }, [assetList]);

  function formatAssetDisplay(assetId) {
    const id = assetId != null ? String(assetId).trim() : "";
    if (!id) return "";
    const asset = assetById[id];
    return asset ? getAssetLabel(asset) : "Unknown asset";
  }

  function openLinkedAssetPeek(assetId, eventOrEl) {
    const id = assetId != null ? String(assetId).trim() : "";
    if (!id) return;
    setLinkedPeek({ type: "asset", id, anchor: getPeekAnchorRect(eventOrEl) });
  }

  function closeLinkedPeek() {
    setLinkedPeek(null);
  }

  function renderLinkedAssetField({ fieldKey, label, linkedId, displayLabel, editor }) {
    const hasLink = !!(linkedId != null && String(linkedId).trim());
    const showEditor = detailMode === "new" || detailActiveField === fieldKey;
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <label className="panel-label">{label}</label>
        {showEditor ? (
          editor
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <button
              type="button"
              data-peek-anchor=""
              onClick={(e) => {
                if (hasLink) openLinkedAssetPeek(linkedId, e);
                else setDetailActiveField(fieldKey);
              }}
              className="btn-link"
              style={{
                textAlign: "left",
                flex: 1,
                minWidth: 0,
                padding: "6px 0",
                color: hasLink ? "#0d6efd" : "#212529",
                textDecoration: hasLink ? "underline" : "none",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={hasLink ? "View asset details" : "Select asset"}
            >
              {displayLabel || "— None"}
            </button>
            <button
              type="button"
              onClick={() => setDetailActiveField(fieldKey)}
              className="btn-link"
              style={iconBtnStyle}
              title="Change asset"
              aria-label="Change asset"
            >
              <IconEdit size={18} />
            </button>
          </div>
        )}
      </div>
    );
  }

  function getDetailFormValues(lease) {
    return {
      property_address: lease?.property_address != null ? String(lease.property_address) : "",
      asset_id: lease?.asset_id != null ? String(lease.asset_id).trim() : "",
      start_date: toYmdString(lease?.start_date),
      end_date: toYmdString(lease?.end_date),
      rent_amount: lease?.rent_amount == null ? "" : String(lease.rent_amount),
      security_deposit: lease?.security_deposit == null ? "" : String(lease.security_deposit),
      pet_deposit: lease?.pet_deposit == null ? "" : String(lease.pet_deposit),
      status: normalizeStatus(lease?.status),
      tenants: normalizeTenantsList(lease?.tenants),
      landlords: lease?.landlords != null ? String(lease.landlords) : "",
    };
  }

  function applyDetailFormValues(values) {
    setDetailPropertyAddress(values?.property_address || "");
    setDetailAssetId(values?.asset_id || "");
    setDetailStartDate(values?.start_date || "");
    setDetailEndDate(values?.end_date || "");
    setDetailRentAmount(normalizeNumber(values?.rent_amount));
    setDetailSecurityDeposit(normalizeNumber(values?.security_deposit));
    setDetailPetDeposit(normalizeNumber(values?.pet_deposit));
    setDetailStatus(normalizeStatus(values?.status));
    setDetailTenants(normalizeTenantsList(values?.tenants));
    setDetailLandlords(values?.landlords || "");
  }

  function openDetail(lease, mode = "view") {
    setDetailMode(mode);
    if (!lease && mode === "new") {
      setDetailId(null);
      const initialValues = getDetailFormValues(null);
      setDetailInitialValues(initialValues);
      applyDetailFormValues(initialValues);
      setDetailActiveField("property_address");
      return;
    }
    if (!lease) {
      setDetailMode("");
      return;
    }
    setDetailId(lease._id);
    const initialValues = getDetailFormValues(lease);
    setDetailInitialValues(initialValues);
    applyDetailFormValues(initialValues);
    setDetailActiveField(mode === "edit" ? "property_address" : "");
  }

  function getDetailPayload() {
    return {
      account_id,
      property_address: (detailPropertyAddress || "").trim(),
      asset_id: (detailAssetId || "").trim(),
      start_date: (detailStartDate || "").trim(),
      end_date: (detailEndDate || "").trim(),
      rent_amount: (detailRentAmount || "").trim(),
      security_deposit: (detailSecurityDeposit || "").trim(),
      pet_deposit: (detailPetDeposit || "").trim(),
      status: normalizeStatus(detailStatus),
      tenants: normalizeTenantsList(detailTenants),
      landlords: (detailLandlords || "").trim(),
    };
  }

  async function persistDetailChanges() {
    const payload = getDetailPayload();
    if (!payload.property_address || !payload.start_date || !payload.end_date) return;
    if (!payload.tenants || payload.tenants.length === 0) return;

    const isNew = detailMode === "new" || !detailId;
    const url = isNew ? serverUrl + "/createRentalLease" : serverUrl + "/updateRentalLease";
    const body = isNew ? payload : { ...payload, _id: detailId };

    try {
      if (isNew) {
        const resp = await apiFetch(url, { method: "POST", body: JSON.stringify(body) });
        const text = await resp.text();
        if (!resp.ok) {
          setBannerError(text || "Request failed.");
          return;
        }
        let data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          data = null;
        }
        if (data && data._id) {
          setDetailId(data._id);
          setDetailMode("edit");
        }
      } else {
        const resp = await apiFetch(url, { method: "POST", body: JSON.stringify(body) });
        const text = await resp.text();
        if (!resp.ok) {
          setBannerError(text || "Request failed.");
          return;
        }
      }
      refreshAll();
      setDetailInitialValues(getDetailFormValues(payload));
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    }
  }

  function deleteDocument(currentObj) {
    const id = currentObj?._id;
    setConfirmDialog({
      open: true,
      title: "Delete rental lease",
      message: "Are you sure you want to delete this lease?",
      onConfirm: async () => {
        try {
          const response = await apiFetch(serverUrl + "/deleteRentalLease", {
            method: "POST",
            body: JSON.stringify({ account_id, _id: id }),
          });
          const data = await response.text();
          if (!response.ok) {
            setBannerError(data || "Delete failed.");
            return;
          }
          setConfirmDialog({ open: true, title: "Rental lease", message: data, mode: "alert", onConfirm: null });
          const newSelectedIds = new Set(selectedIds);
          newSelectedIds.delete(id);
          setSelectedIds(newSelectedIds);
          refreshAll();
        } catch (err) {
          setBannerError(getMessageForFetchError(err));
        }
      },
    });
  }

  function matchesLeaseListFilters(l) {
    const searchLower = (searchTerm || "").toLowerCase().trim();
    const stat = (statusFilter || "").trim();
    if (stat && normalizeStatus(l.status) !== stat) return false;
    if (!searchLower) return true;

    const address = (l.property_address || "").toLowerCase();
    const tenantList = normalizeTenantsList(l.tenants);
    const tenants = tenantList
      .map((t) => [t.full_name, t.email, t.phone].filter(Boolean).join(" "))
      .join(" ")
      .toLowerCase();
    const landlords = (l.landlords || "").toLowerCase();
    const assetLabel = formatAssetDisplay(l.asset_id).toLowerCase();
    return (
      address.includes(searchLower) ||
      tenants.includes(searchLower) ||
      landlords.includes(searchLower) ||
      assetLabel.includes(searchLower) ||
      normalizeStatus(l.status).toLowerCase().includes(searchLower) ||
      String(l.rent_amount ?? "").toLowerCase().includes(searchLower) ||
      toYmdString(l.start_date).includes(searchLower) ||
      toYmdString(l.end_date).includes(searchLower)
    );
  }

  const expiredQuickFilterCount = useMemo(() => {
    let count = 0;
    for (const l of leases || []) {
      if (!matchesLeaseListFilters(l)) continue;
      if (isLeaseExpiredByEndDate(l?.end_date)) count += 1;
    }
    return count;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- count follows search/status list filters
  }, [leases, searchTerm, statusFilter]);

  function getFilteredAndSortedData() {
    const filtered = (leases || []).filter((l) => {
      if (!matchesLeaseListFilters(l)) return false;
      if (expirationQuickFilter === "expired" && !isLeaseExpiredByEndDate(l?.end_date)) {
        return false;
      }
      return true;
    });
    return sortRowsByConfig(filtered, sortConfig, RENTAL_LEASE_SORT_KEY_TYPES);
  }

  const filteredRows = getFilteredAndSortedData();
  const listSummary = summarizeLeaseRent(filteredRows);
  const expiredListRows = useMemo(() => {
    return (leases || []).filter(
      (l) => matchesLeaseListFilters(l) && isLeaseExpiredByEndDate(l?.end_date)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follows search/status list filters
  }, [leases, searchTerm, statusFilter]);
  const expiredListSummary = summarizeLeaseRent(expiredListRows);
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  function openBulkUpdate() {
    if (selectedVisibleCount === 0) {
      setConfirmDialog({
        open: true,
        title: "Bulk update",
        message: "Please select at least one lease to update",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setBulkUpdateOpen(true);
  }

  async function applyBulkUpdate(fields) {
    if (selectedVisibleCount === 0) {
      setBulkUpdateOpen(false);
      return;
    }
    const ids = Array.from(selectedVisibleIds);
    try {
      setBulkUpdating(true);
      const res = await bulkUpdateRentalLeases({ account_id, _ids: ids, ...fields });
      const matched = res && typeof res.matched === "number" ? res.matched : ids.length;
      const modified = res && typeof res.modified === "number" ? res.modified : 0;
      setConfirmDialog({
        open: true,
        title: "Bulk update completed",
        mode: "alert",
        onConfirm: null,
        message: `Updated ${modified} lease(s) (matched ${matched}).`,
      });
      setSelectedIds(new Set());
      setBulkUpdateOpen(false);
      refreshAll();
    } catch (err) {
      setConfirmDialog({
        open: true,
        title: "Bulk update",
        message: err?.message || "Bulk update failed",
        mode: "alert",
        onConfirm: null,
      });
    } finally {
      setBulkUpdating(false);
    }
  }

  async function deleteSelected() {
    if (selectedVisibleCount === 0) {
      setConfirmDialog({ open: true, title: "Delete leases", message: "Please select at least one record to delete", mode: "alert", onConfirm: null });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete leases",
      message: `Are you sure you want to delete ${selectedVisibleCount} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (_id) => {
            const response = await apiFetch(serverUrl + "/deleteRentalLease", {
              method: "POST",
              body: JSON.stringify({ account_id, _id }),
            });
            const data = await response.text();
            return { ok: response.ok, body: data };
          },
          getMessageForFetchError
        );
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          onConfirm: null,
          mode: "alert",
        });
        setSelectedIds(new Set());
        refreshAll();
        setDeleting(false);
      },
    });
  }

  const detailCurrentValues = useMemo(
    () => ({
      property_address: (detailPropertyAddress || "").trim(),
      asset_id: (detailAssetId || "").trim(),
      start_date: (detailStartDate || "").trim(),
      end_date: (detailEndDate || "").trim(),
      rent_amount: (detailRentAmount || "").trim(),
      security_deposit: (detailSecurityDeposit || "").trim(),
      pet_deposit: (detailPetDeposit || "").trim(),
      status: normalizeStatus(detailStatus),
      tenants: normalizeTenantsList(detailTenants),
      landlords: (detailLandlords || "").trim(),
    }),
    [
      detailAssetId,
      detailEndDate,
      detailLandlords,
      detailPetDeposit,
      detailPropertyAddress,
      detailRentAmount,
      detailSecurityDeposit,
      detailStartDate,
      detailStatus,
      detailTenants,
    ]
  );

  const detailHasChanges =
    !!detailInitialValues && JSON.stringify(detailCurrentValues) !== JSON.stringify(detailInitialValues);

  const { isSaving: detailSaving, handleFieldBlur, handleFieldCommitKeyDown, saveIfNeeded } = useBlurAutoSave({
    hasChanges: detailHasChanges,
    saveFn: persistDetailChanges,
    onExitEdit: () => setDetailActiveField(""),
  });

  const detailAutoSaveEnabled = detailMode !== "new";
  const detailFieldBlurHandler = detailAutoSaveEnabled ? handleFieldBlur : undefined;
  const detailFieldCommitKeyDownHandler = detailAutoSaveEnabled ? handleFieldCommitKeyDown : undefined;

  const detailMissingRequired = !(
    detailCurrentValues.property_address &&
    detailCurrentValues.start_date &&
    detailCurrentValues.end_date &&
    detailCurrentValues.tenants.length > 0
  );

  function openTenantsModalForLease(lease) {
    if (!lease?._id) return;
    setTenantsModalLeaseId(String(lease._id));
    setTenantsModalOpen(true);
  }

  function openTenantsModalForDetail() {
    setTenantsModalLeaseId(detailId ? String(detailId) : null);
    setTenantsModalOpen(true);
  }

  function tenantsModalSourceTenants() {
    if (tenantsModalLeaseId) {
      const lease = (leases || []).find((l) => String(l._id) === String(tenantsModalLeaseId));
      if (lease) return normalizeTenantsList(lease.tenants);
      if (detailId && String(detailId) === String(tenantsModalLeaseId)) {
        return normalizeTenantsList(detailTenants);
      }
    }
    return normalizeTenantsList(detailTenants);
  }

  async function saveTenantsFromModal(nextTenants) {
    const validated = validateTenantsList(nextTenants);
    if (!validated.ok) throw new Error(validated.error);

    const leaseId = tenantsModalLeaseId;
    if (leaseId) {
      setTenantsModalSaving(true);
      try {
        const resp = await apiFetch(serverUrl + "/updateRentalLeaseTenants", {
          method: "POST",
          body: JSON.stringify({ account_id, _id: leaseId, tenants: validated.tenants }),
        });
        const text = await resp.text();
        if (!resp.ok) throw new Error(text || "Failed to save tenants");
        setLeases((prev) =>
          (prev || []).map((l) =>
            String(l._id) === String(leaseId) ? { ...l, tenants: validated.tenants } : l
          )
        );
        if (detailId && String(detailId) === String(leaseId)) {
          setDetailTenants(validated.tenants);
          setDetailInitialValues((prev) => (prev ? { ...prev, tenants: validated.tenants } : prev));
        }
      } finally {
        setTenantsModalSaving(false);
      }
      return;
    }

    setDetailTenants(validated.tenants);
    setDetailInitialValues((prev) => (prev ? { ...prev, tenants: validated.tenants } : prev));
  }

  function parseRecordsFromCsvText(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);
    const idx = (col) => findLeaseCsvColumnIndex(headers, col);
    const addressIdx = idx("property_address");
    if (addressIdx < 0) throw new Error("CSV must contain a Property Address column");
    const tenantsIdx = idx("tenants");
    const landlordsIdx = idx("landlords");
    const statusIdx = idx("status");
    const startIdx = idx("start_date");
    const endIdx = idx("end_date");
    const rentIdx = idx("rent_amount");
    const secIdx = idx("security_deposit");
    const petIdx = idx("pet_deposit");
    const assetIdx = idx("asset_id");

    return (rows || []).map((values) => ({
      property_address: values[addressIdx] ?? "",
      tenants: parseTenantsDisplayText(tenantsIdx >= 0 ? values[tenantsIdx] ?? "" : ""),
      landlords: landlordsIdx >= 0 ? values[landlordsIdx] ?? "" : "",
      status: statusIdx >= 0 ? values[statusIdx] ?? "Upcoming" : "Upcoming",
      start_date: startIdx >= 0 ? values[startIdx] ?? "" : "",
      end_date: endIdx >= 0 ? values[endIdx] ?? "" : "",
      rent_amount: rentIdx >= 0 ? values[rentIdx] ?? "" : "",
      security_deposit: secIdx >= 0 ? values[secIdx] ?? "" : "",
      pet_deposit: petIdx >= 0 ? values[petIdx] ?? "" : "",
      asset_id: assetIdx >= 0 ? values[assetIdx] ?? "" : "",
    }));
  }

  async function importFromCSV(file) {
    if (!file) {
      setConfirmDialog({ open: true, title: "Import", message: "Please select a CSV file", mode: "alert", onConfirm: null });
      return;
    }
    const text = await file.text();
    await importFromCsvText(text);
  }

  async function importFromCsvText(text) {
    setImporting(true);
    try {
      const records = parseRecordsFromCsvText(text);
      if (records.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV file", mode: "alert", onConfirm: null });
        setImporting(false);
        return;
      }
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        const property_address = (record.property_address && String(record.property_address).trim()) || "";
        if (!property_address) {
          errorCount++;
          errors.push(`Row ${i + 2}: Missing property address`);
          continue;
        }
        const tenants = Array.isArray(record.tenants)
          ? record.tenants
          : parseTenantsDisplayText(record.tenants);
        const tenantCheck = validateTenantsList(tenants);
        if (!tenantCheck.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${tenantCheck.error}`);
          continue;
        }
        const startParsed = parseCsvDateToIso(record.start_date);
        if (!startParsed.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: start_date: ${startParsed.error}`);
          continue;
        }
        const endParsed = parseCsvDateToIso(record.end_date);
        if (!endParsed.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: end_date: ${endParsed.error}`);
          continue;
        }
        const rentParsed = parseOptionalFiniteNumber(record.rent_amount, { min: 0, fieldName: "rent_amount" });
        if (!rentParsed.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${rentParsed.error}`);
          continue;
        }
        const secParsed = parseOptionalFiniteNumber(record.security_deposit, { min: 0, fieldName: "security_deposit" });
        if (!secParsed.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${secParsed.error}`);
          continue;
        }
        const petParsed = parseOptionalFiniteNumber(record.pet_deposit, { min: 0, fieldName: "pet_deposit" });
        if (!petParsed.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${petParsed.error}`);
          continue;
        }
        const status = normalizeStatus(record.status);
        try {
          const response = await apiFetch(serverUrl + "/createRentalLease", {
            method: "POST",
            body: JSON.stringify({
              account_id,
              property_address,
              tenants: tenantCheck.tenants,
              landlords: (record.landlords != null ? String(record.landlords) : "").trim(),
              status,
              start_date: startParsed.iso,
              end_date: endParsed.iso,
              rent_amount: rentParsed.value !== undefined ? rentParsed.value : "",
              security_deposit: secParsed.value !== undefined ? secParsed.value : "",
              pet_deposit: petParsed.value !== undefined ? petParsed.value : "",
              asset_id: (record.asset_id != null ? String(record.asset_id) : "").trim(),
            }),
          });
          const data = await response.text();
          if (response.ok) successCount++;
          else {
            errorCount++;
            errors.push(`Row ${i + 2}: ${data}`);
          }
        } catch (error) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${getMessageForFetchError(error)}`);
        }
      }
      setConfirmDialog({
        open: true,
        title: "Import completed",
        message: formatBatchSummaryMessage("Import completed!", successCount, errorCount, errors),
        onConfirm: null,
        mode: "alert",
      });
      refreshAll();
    } catch (error) {
      setBannerError(getMessageForFetchError(error, "Error importing CSV."));
    } finally {
      setImporting(false);
    }
  }

  function handleFileChange(e) {
    attachValidatedCsvFileImport(e, importFromCSV, (msg) => {
      setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
    });
  }

  function downloadSampleCSV() {
    downloadCsvTextFile("rental-leases-sample.csv", [
      RENTAL_LEASE_CSV_HEADER_LINE,
      '"921 White Dove Dr, Arlington, Texas 76017","Bill Daniel Pelfrey, Becky Eileen Pelfrey","Backiaraj Irudayaraj, Joyce Irudayaraj",Active,2025-05-16,2027-06-30,2600,2600,1000,',
    ]);
  }

  function exportSelectedLeasesCsv() {
    if (selectedVisibleCount === 0) {
      setConfirmDialog({
        open: true,
        title: "Export",
        message: "Select at least one lease to export.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setExporting(true);
    try {
      const rows = (leases || []).filter((row) => row && selectedVisibleIds.has(row._id));
      downloadCsvTextFromString("rental-leases-export.csv", buildRentalLeaseCsvExportText(rows));
    } catch (err) {
      setBannerError(getMessageForFetchError(err, "Error exporting CSV."));
    } finally {
      setExporting(false);
    }
  }

  function renderColumnCell(colId, row) {
    switch (colId) {
      case "property_address":
        return (
          <button type="button" className="btn-link" onClick={() => openDetail(row, "view")}>
            {row.property_address || "—"}
          </button>
        );
      case "status":
        return normalizeStatus(row.status);
      case "start_date":
        return formatDueDate(row.start_date) || "—";
      case "end_date": {
        const label = formatDueDate(row.end_date) || "—";
        if (label === "—") return label;
        if (isLeaseExpiredByEndDate(row.end_date)) {
          return <span className="table-cell-expired">{label}</span>;
        }
        return label;
      }
      case "rent_amount":
        return formatLeaseMoney(row.rent_amount);
      case "security_deposit":
        return formatLeaseMoney(row.security_deposit);
      case "pet_deposit":
        return formatLeaseMoney(row.pet_deposit);
      case "tenants":
        return (
          <button
            type="button"
            className="btn-link"
            style={{ textAlign: "left", whiteSpace: "pre-wrap", fontWeight: 500 }}
            onClick={() => openTenantsModalForLease(row)}
            title="Manage tenants"
          >
            {formatTenantNamesDisplay(row.tenants)}
          </button>
        );
      case "landlords":
        return <span style={{ whiteSpace: "pre-wrap" }}>{row.landlords || "—"}</span>;
      case "asset_id": {
        const assetLabel = formatAssetDisplay(row.asset_id);
        if (row.asset_id && assetLabel) {
          return (
            <button type="button" className="btn-link" onClick={(e) => openLinkedAssetPeek(row.asset_id, e)}>
              {assetLabel}
            </button>
          );
        }
        return "—";
      }
      default:
        return row?.[colId] != null && String(row[colId]).trim() !== "" ? String(row[colId]) : "—";
    }
  }

  function renderClickableTextField(fieldKey, label, value, editor, opts = {}) {
    const { fullWidth = false, multiline = false } = opts;
    return (
      <div style={{ display: "flex", flexDirection: "column", ...(fullWidth ? { gridColumn: "1 / span 2" } : {}) }}>
        <label className="panel-label">{label}</label>
        {detailMode === "new" || detailActiveField === fieldKey ? (
          editor
        ) : (
          <button
            type="button"
            onClick={() => setDetailActiveField(fieldKey)}
            className="btn-link"
            style={{
              textAlign: "left",
              width: "100%",
              padding: "6px 0",
              color: "#212529",
              textDecoration: "none",
              fontWeight: 500,
              whiteSpace: multiline ? "pre-wrap" : "normal",
            }}
          >
            {value || "—"}
          </button>
        )}
      </div>
    );
  }

  return (
    <EntityPageScaffold
      title="Rental Leases"
      titleTabs={<AssetManagementSectionTabs activeTab={ASSET_TAB_RENTAL_LEASES} />}
      subtitle="Capture and manage rental leases."
      bannerError={bannerError}
      onDismissBanner={() => setBannerError("")}
      confirmDialog={confirmDialog}
      onConfirmModal={() => {
        const fn = confirmDialog.onConfirm;
        closeConfirmDialog();
        if (fn) fn();
      }}
      onCancelModal={closeConfirmDialog}
      pageTopAfterModal={
        <>
          <ColumnCustomizerModal
            open={columnModalOpen}
            onClose={() => setColumnModalOpen(false)}
            allColumns={RENTAL_LEASE_TABLE_COLUMNS}
            visibleIds={visibleColumnIds}
            onChangeVisibleIds={setVisibleColumnIds}
            saving={columnPrefsSaving}
            onReset={async () => {
              const defaultIds = [...RENTAL_LEASE_TABLE_DEFAULT_VISIBLE];
              setVisibleColumnIds(defaultIds);
              if (!account_id || !currentUserId) {
                setColumnModalOpen(false);
                return;
              }
              try {
                setColumnPrefsSaving(true);
                await saveTablePreferences(account_id, currentUserId, RENTAL_LEASE_TABLE_KEY, defaultIds);
              } catch (err) {
                setConfirmDialog({
                  open: true,
                  title: "Column preferences",
                  message: err?.message || "Failed to reset column preferences",
                  mode: "alert",
                  onConfirm: null,
                });
              } finally {
                setColumnPrefsSaving(false);
              }
              setColumnModalOpen(false);
            }}
            onSave={async (ids) => {
              if (!account_id || !currentUserId) {
                setVisibleColumnIds(ids);
                setColumnModalOpen(false);
                return;
              }
              try {
                setColumnPrefsSaving(true);
                await saveTablePreferences(account_id, currentUserId, RENTAL_LEASE_TABLE_KEY, ids);
                setVisibleColumnIds(ids);
                setColumnModalOpen(false);
              } catch (err) {
                setConfirmDialog({
                  open: true,
                  title: "Column preferences",
                  message: err?.message || "Failed to save column preferences",
                  mode: "alert",
                  onConfirm: null,
                });
              } finally {
                setColumnPrefsSaving(false);
              }
            }}
          />

          {(detailMode === "view" || detailMode === "edit" || detailMode === "new") && (
            <div className="detail-modal-overlay">
              <div
                role="button"
                tabIndex={0}
                className="detail-modal-backdrop"
                onClick={() => setDetailMode("")}
                onKeyDown={(e) => e.key === "Escape" && setDetailMode("")}
                aria-label="Close"
              />
              <div className="panel detail-modal-panel" style={{ maxWidth: 980 }}>
                <div className="panel-row" style={{ justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #dee2e6", padding: "12px 16px" }}>
                  <h3 className="panel-title" style={{ margin: 0, fontSize: "1.1rem" }}>
                    {detailMode === "new" ? "New lease" : "Lease details"}
                  </h3>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {detailMode === "new" && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={detailMissingRequired || detailSaving}
                        onClick={async () => {
                          await saveIfNeeded();
                        }}
                      >
                        Create
                      </button>
                    )}
                    <DetailSaveStatus isSaving={detailSaving} hasChanges={detailHasChanges} />
                    <button type="button" className="btn-link btn-link-muted" onClick={() => setDetailMode("")} style={{ fontSize: "18px", lineHeight: 1, padding: "4px 8px" }} aria-label="Close">
                      ×
                    </button>
                  </div>
                </div>

                <div style={{ padding: "12px 16px", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px 16px" }}>
                  {renderClickableTextField(
                    "property_address",
                    "Property address",
                    detailPropertyAddress,
                    <input
                      autoFocus={detailMode === "new" || detailActiveField === "property_address"}
                      type="text"
                      value={detailPropertyAddress}
                      onChange={(e) => setDetailPropertyAddress(e.target.value)}
                      onBlur={detailFieldBlurHandler}
                      onKeyDown={detailFieldCommitKeyDownHandler}
                      className="input input-full"
                      placeholder="Street, City, State ZIP"
                    />,
                    { fullWidth: true }
                  )}

                  {renderLinkedAssetField({
                    fieldKey: "asset_id",
                    label: "Asset",
                    linkedId: detailAssetId,
                    displayLabel: formatAssetDisplay(detailAssetId),
                    editor: (
                      <SearchableSelect
                        value={detailAssetId || ""}
                        onChange={(v) => setDetailAssetId(String(v || ""))}
                        options={(assetList || []).map((a) => ({
                          value: String(a._id),
                          label: getAssetLabel(a),
                          keywords: [a.name, a.category, a.address].filter(Boolean).join(" "),
                        }))}
                        placeholder="Select investment home…"
                        searchPlaceholder="Search investment home…"
                        allowEmpty={true}
                        emptyLabel="— None"
                        maxVisibleItems={4}
                        dropdownPortal={true}
                        title="Asset"
                        autoFocus
                        onBlur={detailFieldBlurHandler}
                        onKeyDown={detailFieldCommitKeyDownHandler}
                      />
                    ),
                  })}

                  {renderClickableTextField(
                    "status",
                    "Status",
                    normalizeStatus(detailStatus),
                    <select
                      autoFocus={detailActiveField === "status"}
                      value={normalizeStatus(detailStatus)}
                      onChange={(e) => setDetailStatus(e.target.value)}
                      onBlur={detailFieldBlurHandler}
                      onKeyDown={detailFieldCommitKeyDownHandler}
                      className="input input-full"
                    >
                      {RENTAL_LEASE_STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  )}

                  {renderClickableTextField(
                    "start_date",
                    "Start date",
                    formatYmdDisplay(detailStartDate),
                    <input
                      autoFocus={detailActiveField === "start_date"}
                      type="date"
                      value={detailStartDate}
                      onChange={(e) => setDetailStartDate(e.target.value)}
                      onBlur={detailFieldBlurHandler}
                      onKeyDown={detailFieldCommitKeyDownHandler}
                      className="input input-full"
                    />
                  )}

                  {renderClickableTextField(
                    "end_date",
                    "End date",
                    formatYmdDisplay(detailEndDate),
                    <input
                      autoFocus={detailActiveField === "end_date"}
                      type="date"
                      value={detailEndDate}
                      onChange={(e) => setDetailEndDate(e.target.value)}
                      onBlur={detailFieldBlurHandler}
                      onKeyDown={detailFieldCommitKeyDownHandler}
                      className="input input-full"
                    />
                  )}

                  {renderClickableTextField(
                    "rent_amount",
                    "Monthly rent",
                    detailRentAmount !== "" ? formatCurrencyUsd(detailRentAmount) : "",
                    <input
                      autoFocus={detailActiveField === "rent_amount"}
                      type="number"
                      min="0"
                      step="0.01"
                      value={detailRentAmount}
                      onChange={(e) => setDetailRentAmount(e.target.value)}
                      onBlur={detailFieldBlurHandler}
                      onKeyDown={detailFieldCommitKeyDownHandler}
                      className="input input-full"
                    />
                  )}

                  {renderClickableTextField(
                    "security_deposit",
                    "Security deposit",
                    detailSecurityDeposit !== "" ? formatCurrencyUsd(detailSecurityDeposit) : "",
                    <input
                      autoFocus={detailActiveField === "security_deposit"}
                      type="number"
                      min="0"
                      step="0.01"
                      value={detailSecurityDeposit}
                      onChange={(e) => setDetailSecurityDeposit(e.target.value)}
                      onBlur={detailFieldBlurHandler}
                      onKeyDown={detailFieldCommitKeyDownHandler}
                      className="input input-full"
                    />
                  )}

                  {renderClickableTextField(
                    "pet_deposit",
                    "Pet deposit",
                    detailPetDeposit !== "" ? formatCurrencyUsd(detailPetDeposit) : "",
                    <input
                      autoFocus={detailActiveField === "pet_deposit"}
                      type="number"
                      min="0"
                      step="0.01"
                      value={detailPetDeposit}
                      onChange={(e) => setDetailPetDeposit(e.target.value)}
                      onBlur={detailFieldBlurHandler}
                      onKeyDown={detailFieldCommitKeyDownHandler}
                      className="input input-full"
                    />
                  )}

                  <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
                    <label className="panel-label">Tenant(s)</label>
                    <button
                      type="button"
                      onClick={openTenantsModalForDetail}
                      className="btn-link"
                      style={{
                        textAlign: "left",
                        width: "100%",
                        padding: "6px 0",
                        color: "#0d6efd",
                        textDecoration: "underline",
                        fontWeight: 500,
                        whiteSpace: "pre-wrap",
                      }}
                      title="Manage tenants"
                    >
                      {formatTenantNamesDisplay(detailTenants)}
                    </button>
                    <div className="panel-hint">Click to add, edit, or remove tenants.</div>
                  </div>

                  {renderClickableTextField(
                    "landlords",
                    "Landlord(s)",
                    detailLandlords,
                    <textarea
                      autoFocus={detailActiveField === "landlords"}
                      value={detailLandlords}
                      onChange={(e) => setDetailLandlords(e.target.value)}
                      onBlur={detailFieldBlurHandler}
                      onKeyDown={detailFieldCommitKeyDownHandler}
                      rows={2}
                      className="input input-full"
                      placeholder="Name, Name2"
                    />,
                    { fullWidth: true, multiline: true }
                  )}
                </div>
              </div>
            </div>
          )}

          <BulkUpdateRentalLeasesModal
            open={bulkUpdateOpen}
            onClose={() => {
              if (bulkUpdating) return;
              setBulkUpdateOpen(false);
            }}
            selectedCount={selectedVisibleCount}
            assets={assetList}
            loading={bulkUpdating}
            onApply={applyBulkUpdate}
          />

          <LeaseTenantsModal
            open={tenantsModalOpen}
            onClose={() => {
              if (tenantsModalSaving) return;
              setTenantsModalOpen(false);
              setTenantsModalLeaseId(null);
            }}
            tenants={tenantsModalSourceTenants()}
            saving={tenantsModalSaving}
            onSave={saveTenantsFromModal}
          />

          <EntityCardPeekOverlay open={!!linkedPeek} onClose={closeLinkedPeek} stacked anchor={linkedPeek?.anchor || null}>
            {linkedPeek?.type === "asset" ? (
              <AssetEntityCard asset={assetById[linkedPeek.id] || { name: "Unknown asset" }} showCheckbox={false} showActions={false} />
            ) : null}
          </EntityCardPeekOverlay>

          <ImportCsvPanel
            expanded={importPanelExpanded}
            fileInputId="rental-lease-csv-import"
            importing={importing}
            columnHint="Columns: property_address (required), tenants (required; display text or Name (EMAIL | phone)), landlords, status (Active|Upcoming|Past), start_date, end_date (YYYY-MM-DD), rent_amount, security_deposit, pet_deposit, asset_id (optional)"
            onDownloadSample={downloadSampleCSV}
            onFileChange={handleFileChange}
            onImportText={importFromCsvText}
          />

          <CrudListToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search address, tenant, landlord, dates, amount…"
            onClearSearch={() => setSearchTerm("")}
            middleSlot={
              <>
                <span className="toolbar-label">
                  <label htmlFor="lease-status-filter">Status:</label>
                </span>
                <select id="lease-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input input-min-130">
                  <option value="">All</option>
                  {RENTAL_LEASE_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={`btn btn-sm ${expirationQuickFilter === "expired" ? "btn-primary-outline active" : "btn-secondary"}`}
                  onClick={() =>
                    setExpirationQuickFilter((prev) => (prev === "expired" ? "" : "expired"))
                  }
                  aria-pressed={expirationQuickFilter === "expired"}
                  title="Show leases whose end date is before today"
                >
                  Expired [{expiredQuickFilterCount}]
                </button>
                <span className="toolbar-divider" />
              </>
            }
            importPanelExpanded={importPanelExpanded}
            onToggleImport={() => setImportPanelExpanded(!importPanelExpanded)}
            extraActions={
              <>
                <button
                  type="button"
                  className="btn btn-md btn-secondary"
                  disabled={selectedVisibleCount === 0 || exporting}
                  onClick={() => exportSelectedLeasesCsv()}
                >
                  {exporting ? "Exporting…" : `Export CSV${selectedCountSuffix}`}
                </button>
                <button
                  type="button"
                  onClick={openBulkUpdate}
                  disabled={selectedVisibleCount === 0 || bulkUpdating}
                  className="btn btn-md btn-muted"
                >
                  Update{selectedCountSuffix}
                </button>
              </>
            }
            onNew={() => openDetail(null, "new")}
            onDeleteSelected={deleteSelected}
            deleteDisabled={selectedVisibleCount === 0 || deleting}
            deleteCountSuffix={selectedCountSuffix}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </>
      }
      list={
        <div className="table-scroll">
          <RentalLeaseSummaryStatCards
            totalRent={listSummary.totalRent}
            leaseCount={filteredRows.length}
            expiredRent={expiredListSummary.totalRent}
            expiredLeaseCount={expiredListRows.length}
          />
          {viewMode === "table" && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th className="text-center">
                      <input
                        type="checkbox"
                        checked={filteredRows.length > 0 && filteredRows.every((obj) => selectedIds.has(obj._id))}
                        onChange={(e) => handleSelectAll(e, getFilteredAndSortedData)}
                        disabled={filteredRows.length === 0}
                      />
                    </th>
                    {visibleColumnIds.map((colId) => {
                      const col = RENTAL_LEASE_TABLE_COLUMNS.find((c) => c.id === colId);
                      const numeric = RENTAL_LEASE_NUMERIC_COLUMN_IDS.has(colId);
                      return (
                        <th
                          key={colId}
                          className={`sortable${numeric ? " table-cell-numeric" : ""}`}
                          onClick={() => handleSort(colId)}
                        >
                          <span className="table-th-label">{col ? col.label : colId}</span>
                        </th>
                      );
                    })}
                    <TableActionColumnHeader
                      onCustomizeColumns={() => setColumnModalOpen(true)}
                      customizeDisabled={columnPrefsSaving}
                    />
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((l) => (
                    <tr key={l._id}>
                      <td className="text-center">
                        <input type="checkbox" checked={selectedIds.has(l._id)} onChange={(e) => handleSelectOne(l._id, e.target.checked)} />
                      </td>
                      {visibleColumnIds.map((colId) => (
                        <td
                          key={colId}
                          className={RENTAL_LEASE_NUMERIC_COLUMN_IDS.has(colId) ? "table-cell-numeric" : undefined}
                        >
                          {renderColumnCell(colId, l)}
                        </td>
                      ))}
                      <td className="text-center">
                        <RowActionIcons>
                          <button type="button" onClick={() => openDetail(l, "edit")} className="btn-link" style={iconBtnStyle} title="Edit">
                            <IconEdit size={18} />
                          </button>
                          <button type="button" onClick={() => deleteDocument(l)} className="btn-link-danger" style={iconBtnStyle} title="Delete">
                            <IconDelete size={18} />
                          </button>
                        </RowActionIcons>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {viewMode === "card" && (
            <div className="cards-wrapper">
              {filteredRows.map((l) => (
                <div key={l._id} className="card">
                  <div className="card-header">
                    <input type="checkbox" checked={selectedIds.has(l._id)} onChange={(e) => handleSelectOne(l._id, e.target.checked)} />
                    <div className="card-header-content">
                      <button type="button" className="btn-link card-title" onClick={() => openDetail(l, "view")}>
                        {l.property_address || "—"}
                      </button>
                      <div className="panel-hint">{normalizeStatus(l.status)}</div>
                    </div>
                  </div>
                  <div className="card-meta-muted">
                    {formatDueDate(l.start_date) || "—"}
                    {" → "}
                    {(() => {
                      const endLabel = formatDueDate(l.end_date) || "—";
                      if (endLabel !== "—" && isLeaseExpiredByEndDate(l.end_date)) {
                        return <span className="date-expired">{endLabel}</span>;
                      }
                      return endLabel;
                    })()}
                    {l.rent_amount != null && l.rent_amount !== "" ? ` · ${formatLeaseMoney(l.rent_amount)}` : ""}
                  </div>
                  <div className="panel-hint" style={{ whiteSpace: "pre-wrap" }}>
                    <button
                      type="button"
                      className="btn-link"
                      style={{ padding: 0, fontWeight: 500, textAlign: "left", whiteSpace: "pre-wrap" }}
                      onClick={() => openTenantsModalForLease(l)}
                      title="Manage tenants"
                    >
                      Tenant(s): {formatTenantNamesDisplay(l.tenants)}
                    </button>
                  </div>
                  {l.landlords ? <div className="panel-hint">Landlord(s): {l.landlords}</div> : null}
                  <div className="card-footer">
                    <RowActionIcons variant="inline">
                    <button type="button" onClick={() => openDetail(l, "edit")} className="btn-link" style={iconBtnStyle} title="Edit">
                      <IconEdit size={18} />
                    </button>
                    <button type="button" onClick={() => deleteDocument(l)} className="btn-link-danger" style={iconBtnStyle} title="Delete">
                      <IconDelete size={18} />
                    </button>
                    </RowActionIcons>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      }
    />
  );
}

export default RentalLease;
